import React, {Suspense, useEffect, useState} from "react";
import {Redirect, Route, Switch, useLocation} from "react-router-dom";
import {Action, Path} from "history";
import {useHistory} from "react-router";
import fetchIntercept, {FetchInterceptorResponse} from "fetch-intercept";
import ProtectedRoute from "./utils/auth/protectedRoute";
import "./main.scss";
import "./i18n";
import {Async} from "react-async";
import {useStores} from "./stores/useStores";
import Layout from "./layout/layout";
import GenericError from "./pages/error/genericError";
import PageNotFound from "./pages/error/pageNotFound";
import SignInRedirect from "./pages/signInRedirect/signInRedirect";
import Events from "./pages/events/events";
import Transactions from "./pages/transactions/transactions";
import Notices from "./pages/notices/notices";
import IndependentResidentAdmin from "./pages/independentResidentAdmin/independentResidentAdmin";
import ServicedApartmentResidentAdmin from "./pages/servicedApartmentResidentAdmin/servicedApartmentResidentAdmin";
import SpecialDays from "./pages/specialDays/specialDays";
import {loadIcons} from "./utils/icons";
import {SkeletonTheme} from "react-loading-skeleton";
import {AppInitializer} from "./utils/appInitializer/appInitializer";
import {AppInsightsErrorBoundary} from "@microsoft/applicationinsights-react-js";
import {ApplicationInsightsTelemetry} from "./utils/telemetry/applicationInsightsTelemetry";
import AccessDenied from "./pages/accessDenied/accessDenied";
import {RoutePaths} from "./shared/routePaths";
import EventCatalogue from "./pages/eventCatalogue/eventCatalogue";
import EditEventTemplate from "./pages/eventCatalogue/bits/editEventTemplate/editEventTemplate";
import EditDeleteEvent from "./pages/events/bits/editDeleteEvent/editDeleteEvent";
import EditNotice from "./pages/notices/bits/editNotice/editNotice";
import CreateEvent from "./pages/events/bits/createEvent/createEvent";
import CreateNotice from "./pages/notices/bits/createNotice/createNotice";
import CreateEventTemplate from "./pages/eventCatalogue/bits/createEventTemplate/createEventTemplate";
import EditSpecialDay from "./pages/specialDays/bits/editSpecialDay/editSpecialDay";
import ViewNotice from "./pages/notices/bits/viewNotice/viewNotice";
import CreateSpecialDay from "./pages/specialDays/bits/createSpecialDay/createSpecialDay";
import MetricsPage from "./pages/metrics/metricsPage";
import VillageCalendars from "./pages/villageCalendars/villageCalendars";
import CreateVillageCalendar from "./pages/villageCalendars/bits/createVillageCalendar/createVillageCalendar";
import EditVillageCalendar from "./pages/villageCalendars/bits/editVillageCalendar/editVillageCalendar";
import SuperUserRoute from "./utils/auth/superUserRoute";

const App: React.FC = () => {
  const [initialPath, setInitialPath] = useState<string>(RoutePaths.Events);

  loadIcons();

  const {
    uiStore: {accountStore, modalStore},
    uiStore,
    domainStore: {personTypesDomainStore}
  } = useStores();

  const location = useLocation();

  function shouldShowSessionExpiredModal(
    originalRequestUrl: string,
    locationPathname: string
  ): boolean {
    const isSignInPage = locationPathname.includes("sign-in");
    const isFirstTimeSignIn =
      locationPathname === "/" && originalRequestUrl !== "/";
    const requestContainsRymanIdentityAccount = originalRequestUrl.includes(
      "RymanIdentity/account/"
    );
    return (
      !isSignInPage &&
      (requestContainsRymanIdentityAccount || !isFirstTimeSignIn)
    );
  }

  // Registers a response interceptor using fetchIntercept,
  // checks for a 401,405 statuses or session expired message in the response text,
  // shows a session expired modal if applicable, and rejects the response to prevent further processing.
  useEffect(() => {
    // Register the interceptor
    let originalRequestUrl = "";
    const unregister = fetchIntercept.register({
      request: function (requestUrl, config) {
        originalRequestUrl = typeof requestUrl === "string" ? requestUrl : "";
        return [requestUrl, config];
      },

      response: function (res) {
        const response = res.clone() as FetchInterceptorResponse;
        response.text().then((errorText) => {
          if (
            (res.status === 401 ||
              res.status === 405 ||
              errorText.includes("MsalUiRequiredException")) &&
            shouldShowSessionExpiredModal(
              originalRequestUrl,
              window.location.pathname
            )
          ) {
            uiStore.modalStore.openSessionExpiredModal();
            return Promise.reject(res);
          }
        });
        return res;
      }
    });

    // Clean up by unregistering the interceptor when the component unmounts
    return () => {
      unregister();
    };
  }, []);

  useEffect(() => {
    uiStore.currentPathName = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const history = useHistory();
  React.useEffect(() => {
    history.listen((path: Path, action: Action) => {
      // If the modal is open and the user hit the back button on the browser, dismiss the modal.
      if (modalStore.isOpen && action === Action.Pop) {
        modalStore.toggle();
      }
    });
  }, [history, modalStore]);

  const doAuthentication = React.useCallback(async () => {
    await accountStore.populateAuthEndpoints();
    await accountStore.authenticate();
    // Load PersonTypes reference data alongside auth so downstream pages
    // (e.g. events, notices, metrics) have the ids available on first render.
    // Failures are non-fatal; the domain store falls back to well-known ids.
    await personTypesDomainStore.loadPersonTypes().catch(() => undefined);
    if (accountStore.abilities.can("Create", "EventCatalogue")) {
      setInitialPath(RoutePaths.EventCatalogue);
    }
  }, [accountStore, personTypesDomainStore]);

  const initializeApp = React.useCallback(async () => {
    await AppInitializer.getInstance().initializeApp();
  }, []);

  return (
    //The colour used here comes from _variables.scss if you need to update it refer to variables for options
    // TODO: Revise the routes in the later release.
    <>
      <div
        className="portal-version"
        dangerouslySetInnerHTML={{
          __html: `<!-- ${process.env.REACT_APP_ADMIN_PORTAL_VERSION} -->`
        }}
      />
      <Suspense fallback="Loading...">
        <Async promiseFn={initializeApp}>
          <Async.Pending>Loading...</Async.Pending>
          <Async.Fulfilled>
            <AppInsightsErrorBoundary
              onError={() => <GenericError />}
              appInsights={
                ApplicationInsightsTelemetry.getInstance().reactPlugin
              }
            >
              <SkeletonTheme color="#edf2f9" highlightColor="#f9fafd">
                <Async
                  promiseFn={doAuthentication}
                  onReject={(err) => {
                    throw err;
                  }}
                >
                  <Async.Pending>Loading...</Async.Pending>
                  <Async.Fulfilled>
                    <Layout>
                      <Switch>
                        <ProtectedRoute exact path={RoutePaths.Home}>
                          <Redirect to={initialPath} />
                        </ProtectedRoute>
                        <ProtectedRoute
                          path={RoutePaths.CreateEvent}
                          component={CreateEvent}
                        />
                        <ProtectedRoute
                          path={RoutePaths.EditEvent}
                          component={EditDeleteEvent}
                        />
                        <ProtectedRoute
                          path={RoutePaths.EditNotice}
                          component={EditNotice}
                          subject="Notice"
                          permissions={["Update", "Delete"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.ViewNotice}
                          component={ViewNotice}
                          subject="Notice"
                          permissions={["Read"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.CreateNotice}
                          component={CreateNotice}
                          subject="Notice"
                          permissions={["Create"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.Notices}
                          component={Notices}
                          subject="Notice"
                          permissions={["Read", "Create", "Update", "Delete"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.Events}
                          component={Events}
                        />
                        <ProtectedRoute
                          path={RoutePaths.Transactions}
                          component={Transactions}
                          subject="ProcessedBooking"
                          permissions={["Read"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.IndependentResidentAdmin}
                          component={IndependentResidentAdmin}
                          subject="ResidentAdmin"
                          permissions={["Create", "Update", "Delete"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.ServicedApartmentResidentAdmin}
                          component={ServicedApartmentResidentAdmin}
                          subject="ResidentAdmin"
                          permissions={["Create", "Update", "Delete"]}
                        />
                        {/* Plain Route: passing children to ProtectedRoute would bypass its
                            auth render logic, and the redirect target is itself protected. */}
                        <Route
                          exact
                          path={RoutePaths.ResidentAdmin}
                          render={({location}) => (
                            <Redirect
                              to={{
                                pathname: RoutePaths.IndependentResidentAdmin,
                                search: location.search
                              }}
                            />
                          )}
                        />
                        <ProtectedRoute
                          path={RoutePaths.EditEventTemplate}
                          component={EditEventTemplate}
                          subject="EventCatalogue"
                          permissions={["Update", "Delete"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.CreateEventTemplate}
                          component={CreateEventTemplate}
                          subject="EventCatalogue"
                          permissions={["Create"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.SpecialDaysCreate}
                          component={CreateSpecialDay}
                          subject="Occasion"
                          permissions={["Create"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.SpecialDaysEdit}
                          component={EditSpecialDay}
                          subject="Occasion"
                          permissions={["Update", "Delete"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.SpecialDays}
                          component={SpecialDays}
                          subject="Occasion"
                          permissions={["Create", "Update", "Delete"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.EventCatalogue}
                          component={EventCatalogue}
                          subject="EventCatalogue"
                          permissions={["Create", "Update", "Delete"]}
                        />
                        <ProtectedRoute
                          path={RoutePaths.Metrics}
                          component={MetricsPage}
                          subject="Metrics"
                          permissions={["Read"]}
                        />
                        <SuperUserRoute
                          path={RoutePaths.VillageCalendarsCreate}
                          component={CreateVillageCalendar}
                        />
                        <SuperUserRoute
                          path={RoutePaths.VillageCalendarsEdit}
                          component={EditVillageCalendar}
                        />
                        <SuperUserRoute
                          path={RoutePaths.VillageCalendars}
                          component={VillageCalendars}
                        />
                        <Route
                          path={RoutePaths.SignIn}
                          component={SignInRedirect}
                        />
                        <Route
                          path={RoutePaths.AccessDenied}
                          component={AccessDenied}
                        />
                        <Route
                          path={RoutePaths.PageNotFound}
                          component={PageNotFound}
                        />
                        <Route
                          path={RoutePaths.GenericError}
                          component={GenericError}
                        />
                        <Route path="*">
                          <Redirect to={initialPath} />
                        </Route>
                      </Switch>
                    </Layout>
                  </Async.Fulfilled>
                </Async>
              </SkeletonTheme>
            </AppInsightsErrorBoundary>
          </Async.Fulfilled>
        </Async>
      </Suspense>
    </>
  );
};

export default App;
